import { useState, useEffect } from "react";
import { apiFetch } from "@/lib/api";
import AdminLogin from "./login";
import BlogManager from "./blog-manager";
import GalleryManager from "./gallery-manager";
import ReviewManager from "./review-manager";
import UserManager from "./user-manager";
import ContactManager from "./contact-manager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, X } from "lucide-react";

type Tab = "blog" | "gallery" | "reviews" | "contacts" | "users";

export default function AdminDashboard() {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<Tab>("blog");
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pwError, setPwError] = useState("");
  const [pwSuccess, setPwSuccess] = useState("");
  const [pwSaving, setPwSaving] = useState(false);

  async function handleChangePassword(e: React.FormEvent) {
    e.preventDefault();
    setPwError("");
    setPwSuccess("");
    if (newPassword !== confirmPassword) {
      setPwError("New passwords do not match");
      return;
    }
    if (newPassword.length < 8) {
      setPwError("New password must be at least 8 characters");
      return;
    }
    setPwSaving(true);
    try {
      await apiFetch("/users/change-password", {
        method: "PUT",
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      setPwSuccess("Password updated successfully!");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setTimeout(() => {
        setShowChangePassword(false);
        setPwSuccess("");
      }, 2000);
    } catch (err: any) {
      setPwError(err.message);
    } finally {
      setPwSaving(false);
    }
  }

  useEffect(() => {
    apiFetch("/auth/me")
      .then((u) => setUser(u))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  async function handleLogout() {
    await apiFetch("/auth/logout", { method: "POST" });
    setUser(null);
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!user) {
    return <AdminLogin onLogin={setUser} />;
  }

  const tabs: { key: Tab; label: string }[] = [
    { key: "blog", label: "Blog Posts" },
    { key: "gallery", label: "Photo Gallery" },
    { key: "reviews", label: "Reviews" },
    { key: "contacts", label: "Contact Submissions" },
    ...(user.role === "owner" ? [{ key: "users" as Tab, label: "Team Members" }] : []),
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-primary text-white px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-serif font-bold">Tallahassee Lassie Admin</h1>
            <p className="text-sm text-primary-foreground/70">
              Welcome, {user.name} ({user.role})
            </p>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => { setShowChangePassword(true); setPwError(""); setPwSuccess(""); }}
              className="text-sm text-primary-foreground/70 hover:text-white flex items-center gap-1"
              title="Change Password"
            >
              <KeyRound className="w-4 h-4" /> Change Password
            </button>
            <a href="/" className="text-sm text-primary-foreground/70 hover:text-white underline">
              View Site
            </a>
            <Button variant="outline" size="sm" onClick={handleLogout} className="text-primary border-white bg-white hover:bg-white/90">
              Logout
            </Button>
          </div>
        </div>
      </header>

      {showChangePassword && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowChangePassword(false)}>
          <div className="bg-white p-6 w-full max-w-md" style={{ borderRadius: "16px 0 16px 0" }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-serif font-bold">Change Password</h3>
              <button onClick={() => setShowChangePassword(false)} className="text-muted-foreground hover:text-foreground">
                <X className="w-5 h-5" />
              </button>
            </div>
            {pwError && <div className="mb-3 p-3 bg-red-50 text-red-600 text-sm rounded-md">{pwError}</div>}
            {pwSuccess && <div className="mb-3 p-3 bg-green-50 text-green-600 text-sm rounded-md">{pwSuccess}</div>}
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <Label htmlFor="currentPw">Current Password</Label>
                <Input id="currentPw" type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required />
              </div>
              <div>
                <Label htmlFor="newPw">New Password</Label>
                <Input id="newPw" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={8} />
              </div>
              <div>
                <Label htmlFor="confirmPw">Confirm New Password</Label>
                <Input id="confirmPw" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required minLength={8} />
              </div>
              <Button type="submit" className="w-full" disabled={pwSaving} style={{ borderRadius: "16px 0 16px 0" }}>
                {pwSaving ? "Updating..." : "Update Password"}
              </Button>
            </form>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-6 py-6">
        <div className="flex gap-1 mb-6 bg-white p-1 rounded-lg shadow-sm w-fit">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === tab.key
                  ? "bg-primary text-white"
                  : "text-muted-foreground hover:bg-gray-100"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === "blog" && <BlogManager />}
        {activeTab === "gallery" && <GalleryManager />}
        {activeTab === "reviews" && <ReviewManager />}
        {activeTab === "contacts" && <ContactManager />}
        {activeTab === "users" && user.role === "owner" && <UserManager />}
      </div>
    </div>
  );
}
